import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Stack,
  Textarea,
  VStack,
  Heading,
  useToast,
  HStack,
  IconButton,
  Divider,
  Text,
  useColorMode,
  Center,
  Spinner,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  AddIcon,
  DeleteIcon,
  AttachmentIcon,
  InfoIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EditIcon,
} from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import ImageUpload from "./ImageUpload";
import {
  createBook,
  getAuthors,
  createAuthor,
  getBookByName,
  getBooksByAuthorId,
} from "../libs/qazbilim-backend";
import {
  uploadDocument,
  convertDocument,
  fetchConvertSupportedTypes,
  splitChaptersText,
} from "../libs/qazbilim-backend";
import { Book, ContentItem } from "../libs/types";
import { useBookStore } from "../store/bookStore";
import { useAuthStore } from "../store/authStore";
import LoadingLogo from "./LoadingLogo";
import Author from "./Author";

/** Display name from upload: strip last extension (handles "a.b.txt" → "a.b"). */
function chapterTitleFromFilename(filename: string): string {
  const name = filename.trim();
  if (!name) return "";
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return name;
  return name.slice(0, dot);
}

const CreateBook: React.FC = () => {
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const { colorMode } = useColorMode();
  const supportedTypesColor = useColorModeValue("gray.600", "gray.400");
  const navigate = useNavigate();
  const location = useLocation();
  const {
    restoreForm: storeRestoreForm,
    setRestoreForm,
    createBookDraft,
    setCreateBookDraft,
    clearCreateBookDraft,
  } = useBookStore();
  const { userId } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const dataRestoredRef = useRef(false); // Track if the restoration toast was already shown

  // Author selection state
  const [authorSearch, setAuthorSearch] = useState("");
  const [availableAuthors, setAvailableAuthors] = useState<any[]>([]);
  const [selectedAuthor, setSelectedAuthor] = useState<any | null>(null);
  const [isSearchingAuthors, setIsSearchingAuthors] = useState(false);
  const [isCreatingAuthor, setIsCreatingAuthor] = useState(false);

  // Book title search state
  const [bookSearch, setBookSearch] = useState("");
  const [availableBooks, setAvailableBooks] = useState<Book[]>([]);
  const [isSearchingBooks, setIsSearchingBooks] = useState(false);
  const [selectedBookFromSearch, setSelectedBookFromSearch] =
    useState<Book | null>(null);
  const [newAuthorData, setNewAuthorData] = useState({
    fullName: "",
    firstName: "",
    lastName: "",
    birthYear: "",
    deathYear: "",
    bio: "",
  });

  const [formData, setFormData] = useState<Omit<Book, "id">>({
    title: "",
    author: "",
    type: "poem", // Default book type
    summary: "",
    coverUrl: "",
    content: [{ chapter: "", text: "" }],
    accessType: "public-domain", // Default access type
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const bulkChaptersFileInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingContentUpload, setIsDraggingContentUpload] = useState(false);
  const [bulkSplitDelimiter, setBulkSplitDelimiter] = useState("");
  const [isBulkAddingChapters, setIsBulkAddingChapters] = useState(false);
  const [convertingChapterIndex, setConvertingChapterIndex] = useState<
    number | null
  >(null);
  const [collapsedChapters, setCollapsedChapters] = useState<
    Record<number, boolean>
  >({});
  const [contentInputMode, setContentInputMode] = useState<"write" | "upload">(
    "write",
  );
  const [chapterUploadAccept, setChapterUploadAccept] = useState<string>("");
  const [chapterUploadTypesLabel, setChapterUploadTypesLabel] = useState<
    string | null
  >(null);
  const [chapterUploadIndex, setChapterUploadIndex] = useState<number | null>(
    null,
  );
  const [pendingChapterUploadIndex, setPendingChapterUploadIndex] = useState<
    number | null
  >(null);
  const [showPreview, setShowPreview] = useState(false);

  // Restore form from preview "Close preview" (Zustand store or location.state)
  useEffect(() => {
    const restore =
      storeRestoreForm ??
      (location.state as { restoreForm?: Book } | null)?.restoreForm;
    if (restore) {
      setFormData({
        author: restore.author ?? "",
        title: restore.title ?? "",
        type: restore.type ?? "poem",
        summary: restore.summary ?? "",
        coverUrl: restore.coverUrl ?? "",
        content: restore.content?.length
          ? restore.content
          : [{ chapter: "", text: "" }],
        accessType: restore.accessType ?? "public-domain",
      });
      if (restore.author?.trim()) {
        setSelectedAuthor({ id: "restored", fullName: restore.author });
      }
      dataRestoredRef.current = true;
      setRestoreForm(null);
    }
  }, [location.state, storeRestoreForm, setRestoreForm]);

  // Restore form from persisted draft (when not returning from preview)
  const hasRestoredFromDraftRef = useRef(false);
  useEffect(() => {
    if (hasRestoredFromDraftRef.current) return;
    if (
      storeRestoreForm ??
      (location.state as { restoreForm?: Book } | null)?.restoreForm
    )
      return;
    const draft = createBookDraft;
    if (draft) {
      setFormData({
        ...draft,
        content: draft.content?.length
          ? draft.content
          : [{ chapter: "", text: "" }],
      });
      hasRestoredFromDraftRef.current = true;
    }
  }, [createBookDraft, storeRestoreForm, location.state]);

  // Persist form to store while editing (cleared on Save/Publish or Clear)
  const hasInitialPersistDoneRef = useRef(false);
  useEffect(() => {
    if (!hasInitialPersistDoneRef.current) {
      hasInitialPersistDoneRef.current = true;
      return;
    }
    const isEmpty =
      !formData.title?.trim() &&
      !formData.author?.trim() &&
      (!formData.content?.length ||
        (formData.content.length === 1 &&
          !formData.content[0].chapter?.trim() &&
          !formData.content[0].text?.trim()));
    setCreateBookDraft(isEmpty ? null : formData);
  }, [formData, setCreateBookDraft]);

  // Debounced author search - search automatically after user stops typing
  useEffect(() => {
    if (!authorSearch.trim()) {
      setAvailableAuthors([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setIsSearchingAuthors(true);
      try {
        const authors = await getAuthors(i18n.language);
        const filtered = authors.filter((author) =>
          author.fullName?.toLowerCase().includes(authorSearch.toLowerCase()),
        );
        setAvailableAuthors(filtered);
      } catch (error) {
        console.error("Error searching authors:", error);
        toast({
          title: "Error",
          description: "Failed to search authors",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsSearchingAuthors(false);
      }
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(searchTimeout);
  }, [authorSearch, i18n.language]);

  useEffect(() => {
    let cancelled = false;
    fetchConvertSupportedTypes()
      .then((d) => {
        if (cancelled) return;
        setChapterUploadAccept(d.accept_markdown);
        setChapterUploadTypesLabel(
          d.markdown_extensions.map((ext) => `.${ext}`).join(", "),
        );
      })
      .catch(() => {
        if (!cancelled) setChapterUploadTypesLabel(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setChapterUploadIndex(null);
  }, [contentInputMode]);

  // Debounced book title search - search automatically after user stops typing
  // Only searches books for the selected author
  useEffect(() => {
    if (!bookSearch.trim()) {
      setAvailableBooks([]);
      return;
    }

    // Only search if an author is selected
    if (!selectedAuthor?.id) {
      setAvailableBooks([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setIsSearchingBooks(true);
      try {
        // Get all books for the selected author
        const authorBooks = await getBooksByAuthorId(
          selectedAuthor.id,
          i18n.language,
          false,
        );

        // Filter by title (case-insensitive)
        const searchTerm = bookSearch.toLowerCase();
        const filtered = authorBooks.filter((book) =>
          book.title?.toLowerCase().includes(searchTerm),
        );

        setAvailableBooks(filtered);
      } catch (error) {
        console.error("Error searching books:", error);
        // Don't show error toast for book search, just log it
        setAvailableBooks([]);
      } finally {
        setIsSearchingBooks(false);
      }
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(searchTimeout);
  }, [bookSearch, i18n.language, selectedAuthor?.id]);

  // Pre-fill fullName with search term when create form appears (only after search completes with no results)
  useEffect(() => {
    if (
      availableAuthors.length === 0 &&
      authorSearch.trim().length > 0 &&
      !isSearchingAuthors &&
      !selectedAuthor
    ) {
      // Only pre-fill if fullName is empty or matches the search (user hasn't edited it)
      if (!newAuthorData.fullName || newAuthorData.fullName === authorSearch) {
        setNewAuthorData((prev) => ({
          ...prev,
          fullName: authorSearch,
        }));
      }
    }
  }, [
    availableAuthors.length,
    authorSearch,
    isSearchingAuthors,
    selectedAuthor,
  ]);

  // Select an author
  const handleSelectAuthor = (author: any) => {
    setSelectedAuthor(author);
    setFormData((prev) => ({
      ...prev,
      author: author.fullName,
    }));
    setAuthorSearch("");
  };

  // Create a new author
  const handleCreateAuthor = async () => {
    if (!newAuthorData.fullName.trim()) {
      toast({
        title: t("createBook.toast.errorTitle"),
        description: "Author full name is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!userId) {
      toast({
        title: t("createBook.toast.errorTitle"),
        description: t(
          "createBook.toast.loginRequired",
          "You must be logged in to create an author",
        ),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsCreatingAuthor(true);
    try {
      const authorData = {
        fullName: newAuthorData.fullName,
        language: i18n.language,
        userId,
        firstName: newAuthorData.firstName || undefined,
        lastName: newAuthorData.lastName || undefined,
        birthYear: newAuthorData.birthYear
          ? parseInt(newAuthorData.birthYear)
          : undefined,
        deathYear: newAuthorData.deathYear
          ? parseInt(newAuthorData.deathYear)
          : undefined,
        bio: newAuthorData.bio || undefined,
      };

      const createdAuthor = await createAuthor(authorData);
      handleSelectAuthor(createdAuthor);

      toast({
        title: "Success",
        description: t(
          "createBook.toast.successTitle",
          "Author created successfully",
        ),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Reset new author form
      setNewAuthorData({
        fullName: "",
        firstName: "",
        lastName: "",
        birthYear: "",
        deathYear: "",
        bio: "",
      });
    } catch (error) {
      console.error("Error creating author:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to create author",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCreatingAuthor(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Update book search when title changes
    if (name === "title") {
      setBookSearch(value);
    }
  };

  // Select a book from search results
  const handleSelectBook = (book: Book) => {
    setFormData((prev) => ({
      ...prev,
      title: book.title,
      author: book.author,
      summary: book.summary || prev.summary,
      coverUrl: book.coverUrl || prev.coverUrl,
      type: book.type || prev.type,
      accessType: book.accessType || prev.accessType,
    }));
    setBookSearch("");
    setAvailableBooks([]);
    setSelectedBookFromSearch(book); // Track that a book was selected

    // If author is set, try to find and select the author
    if (book.author) {
      // Trigger author search to find matching author
      setAuthorSearch(book.author);
    }
  };

  // Clear book selection and reset form fields that were filled by book selection
  const handleClearBookSelection = () => {
    setFormData((prev) => ({
      ...prev,
      title: "",
      summary: "",
      coverUrl: "",
      type: "poem", // Reset to default
      accessType: "public-domain", // Reset to default
    }));
    setSelectedBookFromSearch(null);
    setBookSearch("");
    setAvailableBooks([]);
  };

  const handleCoverImageSelect = async (file: File | null) => {
    setCoverFile(file);

    // If removing the image
    if (!file) {
      setFormData((prev) => ({ ...prev, coverUrl: "" }));
      return;
    }

    // Auto-upload the image when selected
    setIsUploadingCover(true);
    try {
      const uploadedUrl = await uploadDocument(file);
      handleCoverImageUpload(uploadedUrl);

      toast({
        title: t("createBook.toast.coverUploadSuccessTitle", "Cover Uploaded"),
        description: t(
          "createBook.toast.coverUploadSuccessDescription",
          "Your cover image has been uploaded successfully",
        ),
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error uploading cover image:", error);
      toast({
        title: t("createBook.toast.coverUploadErrorTitle", "Upload Failed"),
        description:
          error instanceof Error
            ? error.message
            : t(
                "createBook.toast.coverUploadErrorDescription",
                "Failed to upload cover image. Please try again.",
              ),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      // Clear the file if upload failed
      setCoverFile(null);
    } finally {
      setIsUploadingCover(false);
    }
  };

  const handleCoverImageUpload = (imageUrl: string) => {
    setFormData((prev) => ({
      ...prev,
      coverUrl: imageUrl,
    }));
  };

  const handleContentChange = (
    index: number,
    field: keyof ContentItem,
    value: string,
  ) => {
    const updatedContent = [...formData.content!];
    updatedContent[index] = {
      ...updatedContent[index],
      [field]: value,
    };

    setFormData((prev) => ({
      ...prev,
      content: updatedContent,
    }));
  };

  const addContentItem = () => {
    let nextIndex = 0;
    setFormData((prev) => {
      const current = prev.content || [];
      nextIndex = current.length;
      return {
        ...prev,
        content: [...current, { chapter: "", text: "" }],
      };
    });
    setCollapsedChapters((prev) => ({ ...prev, [nextIndex]: false }));
  };

  const removeContentItem = (index: number) => {
    const updatedContent =
      formData.content?.filter((_, i) => i !== index) ?? [];
    setFormData((prev) => ({
      ...prev,
      content: updatedContent,
    }));

    setCollapsedChapters((prevCollapsed) => {
      const nextCollapsed: Record<number, boolean> = {};
      updatedContent.forEach((_, idx) => {
        nextCollapsed[idx] = prevCollapsed[idx] ?? true;
      });
      return nextCollapsed;
    });

    setChapterUploadIndex((prev) => {
      if (prev === null) return null;
      if (prev === index) return null;
      if (prev > index) return prev - 1;
      return prev;
    });
  };

  const toggleChapterCollapsed = (index: number) => {
    setCollapsedChapters((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  // Same behavior as UpdateBook: convert to markdown, fill empty chapter title from filename.
  const appendFilesToChapter = async (
    files: File[] | FileList,
    chapterIndex: number,
  ) => {
    if (isBulkAddingChapters) return;
    setConvertingChapterIndex(chapterIndex);
    try {
      for (const file of Array.from(files)) {
        const converted = await convertDocument(file, {
          output_type: "markdown",
        });
        const text = typeof converted === "string" ? converted : "";
        if (!text) continue;
        const titleStem = chapterTitleFromFilename(file.name);
        setFormData((prev) => {
          const content = [...(prev.content || [])];
          const existing = content[chapterIndex]?.text || "";
          if (!content[chapterIndex]) {
            content[chapterIndex] = { chapter: "", text: "" };
          }
          const prevChapter = (content[chapterIndex].chapter || "").trim();
          content[chapterIndex] = {
            ...content[chapterIndex],
            chapter: prevChapter || titleStem,
            text: existing ? `${existing}\n\n${text}` : text,
          };
          return { ...prev, content };
        });
      }
    } catch (err) {
      console.error("Chapter upload convert failed", err);
      const first = Array.from(files)[0] as File | undefined;
      toast({
        title: t("createBook.toast.errorTitle"),
        description: t(
          "createBook.content.convertFailed",
          "Failed to extract text from {{name}}",
          { name: first?.name ?? "" },
        ),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setConvertingChapterIndex(null);
    }
  };

  const addChaptersFromUploadedFiles = async (files: File[]) => {
    if (files.length === 0) return;
    const startIndex = (formData.content || []).length;
    setIsBulkAddingChapters(true);
    const newItems: ContentItem[] = [];
    let skipped = 0;

    const useSplit = bulkSplitDelimiter.trim().length > 0;

    try {
      for (const file of files) {
        try {
          const converted = await convertDocument(file, {
            output_type: "markdown",
          });
          const text = typeof converted === "string" ? converted : "";
          if (!text.trim()) {
            skipped += 1;
            continue;
          }

          if (useSplit) {
            const chapters = await splitChaptersText(text, {
              delimiter: bulkSplitDelimiter.trim(),
            });
            const baseTitle = chapterTitleFromFilename(file.name);
            let addedFromFile = 0;
            chapters.forEach(
              (item: { chapter: string; text: string }, i: number) => {
                const body = (item.text || "").trim();
                if (!body) return;
                const titleRaw = (item.chapter || "").trim();
                const chapter = titleRaw || `${baseTitle} — ${i + 1}`;
                newItems.push({ chapter, text: body });
                addedFromFile += 1;
              },
            );
            if (addedFromFile === 0) {
              skipped += 1;
            }
          } else {
            newItems.push({
              chapter: chapterTitleFromFilename(file.name),
              text,
            });
          }
        } catch {
          skipped += 1;
        }
      }

      if (newItems.length > 0) {
        setFormData((prev) => ({
          ...prev,
          content: [...(prev.content || []), ...newItems],
        }));
        setCollapsedChapters((prev) => {
          const next = { ...prev };
          newItems.forEach((_, i) => {
            next[startIndex + i] = false;
          });
          return next;
        });
      }

      if (newItems.length === 0) {
        toast({
          title: t("updateBook.bulkImportFailedTitle", "No chapters imported"),
          description: t(
            "updateBook.bulkImportFailedDescription",
            "No new chapters were added. Check supported file types and that files contain extractable text.",
          ),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else if (skipped > 0) {
        toast({
          title: t(
            "updateBook.bulkImportPartialTitle",
            "Import finished with issues",
          ),
          description: t(
            "updateBook.bulkImportPartialDescription",
            "{{added}} chapters added; {{skipped}} file(s) failed or had no text.",
            { added: newItems.length, skipped },
          ),
          status: "warning",
          duration: 6000,
          isClosable: true,
        });
      } else {
        toast({
          title: t("updateBook.bulkImportSuccessTitle", "Chapters imported"),
          description: t(
            "updateBook.bulkImportSuccessDescription",
            "{{count}} chapters added from files.",
            { count: newItems.length },
          ),
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      }
    } finally {
      setIsBulkAddingChapters(false);
    }
  };

  const handleBulkChaptersFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = e.target.files;
    e.target.value = "";
    if (!files?.length) return;
    await addChaptersFromUploadedFiles(Array.from(files));
  };

  const handleContentFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = e.target.files;
    if (!files?.length) return;

    if (pendingChapterUploadIndex !== null) {
      const targetIndex = pendingChapterUploadIndex;
      setPendingChapterUploadIndex(null);
      await appendFilesToChapter(files, targetIndex);
      e.target.value = "";
      return;
    }

    e.target.value = "";
  };

  const handleContentUploadDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingContentUpload(true);
  };

  const handleContentUploadDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleContentUploadDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingContentUpload(false);
  };

  const handleBulkChaptersUploadDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingContentUpload(false);
    if (isBulkAddingChapters || !e.dataTransfer.files?.length) return;
    await addChaptersFromUploadedFiles(Array.from(e.dataTransfer.files));
  };

  const handleChapterUploadDrop = async (
    e: React.DragEvent,
    chapterIndex: number,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingContentUpload(false);
    if (isBulkAddingChapters || !e.dataTransfer.files?.length) return;
    await appendFilesToChapter(e.dataTransfer.files, chapterIndex);
  };

  // Function to clear the form and cache
  const clearFormAndCache = () => {
    clearCreateBookDraft();
    // Clear book search state
    setBookSearch("");
    setAvailableBooks([]);
    setSelectedBookFromSearch(null);
    // Clear form data
    setFormData({
      title: "",
      author: "",
      type: "poem", // Default book type
      summary: "",
      coverUrl: "",
      content: [{ chapter: "", text: "" }],
      accessType: "public-domain", // Default access type
    });

    // Clear author selection
    setSelectedAuthor(null);
    setAuthorSearch("");
    setNewAuthorData({
      fullName: "",
      firstName: "",
      lastName: "",
      birthYear: "",
      deathYear: "",
      bio: "",
    });

    // Clear cover file
    setCoverFile(null);

    setCollapsedChapters({});
    setContentInputMode("write");
    setConvertingChapterIndex(null);

    // Reset the data restored ref so it can show again if needed
    dataRestoredRef.current = false;

    // Show confirmation toast
    toast({
      title: t("createBook.toast.dataClearedTitle", "Form Cleared"),
      description: t(
        "createBook.toast.dataClearedDescription",
        "Your form data has been cleared",
      ),
      status: "info",
      duration: 4000, // Increased from 2000 to 4000 ms
      isClosable: true,
    });
  };

  const handleCancel = () => {
    clearFormAndCache();
  };

  const handlePreview = () => {
    const draftBook: Book = {
      id: "preview",
      title: formData.title || t("createBook.preview.untitled", "Untitled"),
      author: formData.author || "",
      type: formData.type || "poem",
      summary: formData.summary || "",
      coverUrl: formData.coverUrl,
      content: formData.content,
      language: i18n.language,
      accessType: formData.accessType,
    };
    navigate("/read/preview", {
      state: { previewBook: draftBook, returnTo: "/create" },
    });
  };

  const handleSubmit = async (e: React.FormEvent, publish: boolean) => {
    e.preventDefault();

    // Require author name (backend uses it for get_or_create_author)
    if (!formData.author?.trim()) {
      toast({
        title: t("createBook.toast.errorTitle"),
        description: t(
          "createBook.toast.authorRequired",
          "Please enter or select an author",
        ),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Make sure cover is uploaded (should be done automatically on file selection)
    if (!formData.coverUrl) {
      toast({
        title: t("createBook.toast.coverRequiredTitle"),
        description: t("createBook.toast.coverRequiredDescription"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const finalContent = [...(formData.content || [])];

      if (!userId) {
        toast({
          title: t("createBook.toast.errorTitle"),
          description: t(
            "createBook.toast.loginRequired",
            "You must be logged in to create a book",
          ),
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setIsSubmitting(false);
        return;
      }

      // Normalize content: backend requires non-empty chapter and text per item
      const normalizedContent: ContentItem[] = finalContent.map((item, i) => ({
        chapter:
          (item.chapter && item.chapter.trim()) ||
          t("createBook.form.chapter", "Chapter") + ` ${i + 1}`,
        text: (item.text && item.text.trim()) || " ",
      }));

      // Create the book with the cover URL (already uploaded via auto-upload)
      const payload = {
        ...formData,
        content: normalizedContent,
        language: i18n.language,
        userId,
        published: publish,
      };
      const newBook = await createBook(payload);

      // Clear the form and cache before leaving
      clearFormAndCache();

      // Go to My books and open the new book
      navigate("/my-books", { state: { openBookId: newBook.id } });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: t("createBook.toast.errorTitle"),
        description:
          error instanceof Error
            ? error.message
            : t("createBook.toast.errorDescription"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isChapterPipelineBusy =
    isBulkAddingChapters || convertingChapterIndex !== null;

  return (
    <Box
      id="create-book-container"
      p={6}
      maxW="container.md"
      mx="auto"
      borderRadius="lg"
      bg="transparent"
      position="relative"
    >
      {/* Full-page loader when submitting */}
      {isSubmitting && (
        <Center
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={10}
          borderRadius="lg"
          bg={colorMode === "light" ? "whiteAlpha.900" : "blackAlpha.800"}
        >
          <VStack spacing={4}>
            <LoadingLogo size="xl" />
            <Text fontWeight="medium">
              {t("createBook.form.submitting", "Submitting")}
            </Text>
          </VStack>
        </Center>
      )}

      <VStack spacing={8} align="stretch">
        {/* Author Section */}
        <Box id="author-section">
          <Heading id="author-section-heading" size="md" mb={4}>
            {t("createBook.author.title", "Author")}
          </Heading>

          {selectedAuthor ? (
            <Author
              author={selectedAuthor}
              onChange={() => {
                setSelectedAuthor(null);
                setFormData((prev) => ({ ...prev, author: "" }));
                setAuthorSearch("");
              }}
            />
          ) : (
            <Box
              id="author-selection-section"
              pt={0}
              pb={4}
              pr={4}
              pl={0}
              borderRadius="md"
            >
              <Input
                id="author-search-input"
                placeholder={t(
                  "createBook.author.searchPlaceholder",
                  "Author (Start typing to search)",
                )}
                value={authorSearch}
                onChange={(e) => setAuthorSearch(e.target.value)}
                variant="outline"
                mb={2}
              />

              {/* Show search results under the search bar */}
              {isSearchingAuthors && (
                <Text
                  id="author-search-loading"
                  fontSize="sm"
                  color="gray.500"
                  mb={2}
                >
                  {t("createBook.author.searching", "Searching...")}
                </Text>
              )}

              {availableAuthors.length > 0 && !isSearchingAuthors && (
                <Box
                  id="author-results-list"
                  maxH="200px"
                  overflowY="auto"
                  borderWidth="1px"
                  borderRadius="md"
                  p={2}
                  mb={2}
                  bg={colorMode === "light" ? "gray.50" : "gray.800"}
                >
                  <Stack spacing={1}>
                    {availableAuthors.map((author) => (
                      <Button
                        key={author.id}
                        id={`author-result-${author.id}`}
                        variant="ghost"
                        size="sm"
                        width="100%"
                        justifyContent="flex-start"
                        onClick={() => handleSelectAuthor(author)}
                      >
                        {author.fullName}
                        {author.birthYear && ` (${author.birthYear})`}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Show create author form if no results found AFTER search completes */}
              {availableAuthors.length === 0 &&
                authorSearch.trim().length > 0 &&
                !isSearchingAuthors && (
                  <Box id="create-author-form" mt={4}>
                    <Text
                      id="create-author-title"
                      fontSize="md"
                      mb={4}
                      fontWeight="medium"
                    >
                      {t(
                        "createBook.author.notFound",
                        "Author not found. Create a new one:",
                      )}
                    </Text>
                    <Stack spacing={4}>
                      <FormControl isRequired>
                        <FormLabel>
                          {t("createBook.author.fullName", "Full Name")}
                        </FormLabel>
                        <Input
                          id="author-full-name-input"
                          value={newAuthorData.fullName}
                          onChange={(e) =>
                            setNewAuthorData((prev) => ({
                              ...prev,
                              fullName: e.target.value,
                            }))
                          }
                          placeholder={t(
                            "createBook.author.fullNamePlaceholder",
                            "Enter full name",
                          )}
                          variant="outline"
                        />
                      </FormControl>

                      <HStack spacing={4}>
                        <FormControl>
                          <FormLabel>
                            {t("createBook.author.firstName", "First Name")}
                          </FormLabel>
                          <Input
                            id="author-first-name-input"
                            value={newAuthorData.firstName}
                            onChange={(e) =>
                              setNewAuthorData((prev) => ({
                                ...prev,
                                firstName: e.target.value,
                              }))
                            }
                            placeholder={t(
                              "createBook.author.firstNamePlaceholder",
                              "Enter first name",
                            )}
                            variant="outline"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>
                            {t("createBook.author.lastName", "Last Name")}
                          </FormLabel>
                          <Input
                            id="author-last-name-input"
                            value={newAuthorData.lastName}
                            onChange={(e) =>
                              setNewAuthorData((prev) => ({
                                ...prev,
                                lastName: e.target.value,
                              }))
                            }
                            placeholder={t(
                              "createBook.author.lastNamePlaceholder",
                              "Enter last name",
                            )}
                            variant="outline"
                          />
                        </FormControl>
                      </HStack>

                      <HStack spacing={4}>
                        <FormControl>
                          <FormLabel>
                            {t("createBook.author.birthYear", "Birth Year")}
                          </FormLabel>
                          <Input
                            id="author-birth-year-input"
                            type="number"
                            value={newAuthorData.birthYear}
                            onChange={(e) =>
                              setNewAuthorData((prev) => ({
                                ...prev,
                                birthYear: e.target.value,
                              }))
                            }
                            placeholder={t(
                              "createBook.author.birthYearPlaceholder",
                              "Enter birth year",
                            )}
                            variant="outline"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>
                            {t("createBook.author.deathYear", "Death Year")}
                          </FormLabel>
                          <Input
                            id="author-death-year-input"
                            type="number"
                            value={newAuthorData.deathYear}
                            onChange={(e) =>
                              setNewAuthorData((prev) => ({
                                ...prev,
                                deathYear: e.target.value,
                              }))
                            }
                            placeholder={t(
                              "createBook.author.deathYearPlaceholder",
                              "Enter death year (if applicable)",
                            )}
                            variant="outline"
                          />
                        </FormControl>
                      </HStack>

                      <FormControl>
                        <FormLabel>
                          {t("createBook.author.bio", "Biography")}
                        </FormLabel>
                        <Textarea
                          id="author-bio-textarea"
                          value={newAuthorData.bio}
                          onChange={(e) =>
                            setNewAuthorData((prev) => ({
                              ...prev,
                              bio: e.target.value,
                            }))
                          }
                          placeholder={t(
                            "createBook.author.bioPlaceholder",
                            "Enter author biography",
                          )}
                          variant="outline"
                          size="md"
                        />
                      </FormControl>

                      <HStack justify="flex-end" spacing={4} pt={2}>
                        <Button
                          id="cancel-create-author-button"
                          variant="outline"
                          onClick={() => {
                            setAuthorSearch("");
                            setNewAuthorData({
                              fullName: "",
                              firstName: "",
                              lastName: "",
                              birthYear: "",
                              deathYear: "",
                              bio: "",
                            });
                          }}
                        >
                          {t("createBook.form.cancel")}
                        </Button>
                        <Button
                          id="create-author-submit-button"
                          colorScheme="blue"
                          onClick={handleCreateAuthor}
                          isLoading={isCreatingAuthor}
                        >
                          {t("createBook.author.create", "Create Author")}
                        </Button>
                      </HStack>
                    </Stack>
                  </Box>
                )}
            </Box>
          )}
        </Box>

        {/* Create New Book Section - Only show if author is selected */}
        {selectedAuthor && (
          <Box id="create-book-section">
            <HStack justify="space-between" mb={4}>
              <Heading id="create-book-heading" size="md">
                {t("createBook.section.title", "Create New Book")}
              </Heading>
              <Button
                id="clear-book-selection-button"
                size="sm"
                variant="outline"
                onClick={handleClearBookSelection}
              >
                {t(
                  "createBook.form.clearBookSelection",
                  "Clear Book Selection",
                )}
              </Button>
            </HStack>
            <form id="create-book-form" onSubmit={(e) => handleSubmit(e, true)}>
              <Stack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>{t("createBook.form.title")}</FormLabel>
                  <Input
                    id="book-title-input"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder={t("createBook.form.titleSearchPlaceholder")}
                    variant="outline"
                    mb={2}
                  />

                  {/* Show search results under the title input */}
                  {isSearchingBooks && (
                    <Text
                      id="book-search-loading"
                      fontSize="sm"
                      color="gray.500"
                      mb={2}
                    >
                      {t("createBook.author.searching", "Searching...")}
                    </Text>
                  )}

                  {availableBooks.length > 0 && !isSearchingBooks && (
                    <Box
                      id="book-results-list"
                      maxH="200px"
                      overflowY="auto"
                      borderWidth="1px"
                      borderRadius="md"
                      p={2}
                      mb={2}
                      bg={colorMode === "light" ? "gray.50" : "gray.800"}
                    >
                      <Stack spacing={1}>
                        {availableBooks.map((book) => (
                          <Button
                            key={book.id}
                            id={`book-result-${book.id}`}
                            variant="ghost"
                            size="sm"
                            width="100%"
                            justifyContent="flex-start"
                            onClick={() => handleSelectBook(book)}
                          >
                            <VStack align="flex-start" spacing={0} width="100%">
                              <Text fontWeight="medium">{book.title}</Text>
                              {book.author && (
                                <Text fontSize="xs" color="gray.500">
                                  by {book.author}
                                </Text>
                              )}
                            </VStack>
                          </Button>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>{t("createBook.form.type")}</FormLabel>
                  <Select
                    id="book-type-select"
                    name="type"
                    value={formData.type || "poem"}
                    onChange={handleChange}
                    variant="outline"
                  >
                    <option value="poem">{t("bookTypes.poem")}</option>
                    <option value="story">{t("bookTypes.story")}</option>
                    <option value="novel">{t("bookTypes.novel")}</option>
                    <option value="essay">{t("bookTypes.essay")}</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>{t("createBook.form.summary")}</FormLabel>
                  <Textarea
                    id="book-summary-textarea"
                    name="summary"
                    value={formData.summary}
                    onChange={handleChange}
                    placeholder={t("createBook.form.summary")}
                    size="md"
                    variant="outline"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>{t("createBook.form.accessType")}</FormLabel>
                  <Select
                    id="book-access-type-select"
                    name="accessType"
                    value={formData.accessType || "public-domain"}
                    onChange={handleChange}
                    variant="outline"
                  >
                    <option value="public-domain">
                      {t("createBook.form.accessTypePublicDomain")}
                    </option>
                    <option value="copyrighted">
                      {t("createBook.form.accessTypeCopyrighted")}
                    </option>
                    <option value="licensed">
                      {t("createBook.form.accessTypeLicensed")}
                    </option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>{t("createBook.form.cover")}</FormLabel>
                  <Box id="book-cover-upload">
                    <ImageUpload
                      onImageSelect={handleCoverImageSelect}
                      onImageUpload={handleCoverImageUpload}
                      existingImageUrl={formData.coverUrl}
                      helpText={t("createBook.form.coverHelp")}
                      bookTitle={formData.title}
                      isUploading={isUploadingCover}
                    />
                  </Box>
                </FormControl>

                <Divider id="content-divider" my={4} />

                <Box id="book-content-section">
                  <HStack justify="space-between" mb={4}>
                    <Text
                      id="content-section-title"
                      fontWeight="bold"
                      fontSize="lg"
                    >
                      {t("createBook.form.content")}
                    </Text>
                  </HStack>

                  {/* Write / Upload mode cards (same pattern as origin/main) */}
                  <HStack spacing={4} mb={6} id="content-mode-cards">
                    <Box
                      as="button"
                      type="button"
                      id="content-mode-write"
                      flex={1}
                      p={6}
                      borderRadius="md"
                      border="none"
                      boxShadow="sm"
                      bg={
                        colorMode === "light"
                          ? "rgba(240, 240, 240, 0.6)"
                          : "rgba(30, 35, 45, 0.6)"
                      }
                      _hover={{
                        bg:
                          colorMode === "light"
                            ? "rgba(235, 235, 235, 0.7)"
                            : "rgba(35, 40, 50, 0.7)",
                        cursor: "pointer",
                      }}
                      cursor="pointer"
                      onClick={() => setContentInputMode("write")}
                      transition="all 0.2s"
                      outline={
                        contentInputMode === "write" ? "2px solid" : undefined
                      }
                      outlineColor={
                        contentInputMode === "write" ? "blue.500" : undefined
                      }
                      outlineOffset="-2px"
                    >
                      <VStack spacing={2}>
                        <EditIcon boxSize={8} />
                        <Text fontWeight="medium">
                          {t("createBook.content.write", "Write")}
                        </Text>
                      </VStack>
                    </Box>
                    <Box
                      as="button"
                      type="button"
                      id="content-mode-upload"
                      flex={1}
                      p={6}
                      borderRadius="md"
                      border="none"
                      boxShadow="sm"
                      bg={
                        colorMode === "light"
                          ? "rgba(240, 240, 240, 0.6)"
                          : "rgba(30, 35, 45, 0.6)"
                      }
                      _hover={{
                        bg:
                          colorMode === "light"
                            ? "rgba(235, 235, 235, 0.7)"
                            : "rgba(35, 40, 50, 0.7)",
                        cursor: "pointer",
                      }}
                      cursor="pointer"
                      onClick={() => setContentInputMode("upload")}
                      transition="all 0.2s"
                      outline={
                        contentInputMode === "upload" ? "2px solid" : undefined
                      }
                      outlineColor={
                        contentInputMode === "upload" ? "blue.500" : undefined
                      }
                      outlineOffset="-2px"
                    >
                      <VStack spacing={2}>
                        <AttachmentIcon boxSize={8} />
                        <Text fontWeight="medium">
                          {t("createBook.content.upload", "Upload")}
                        </Text>
                      </VStack>
                    </Box>
                  </HStack>

                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={chapterUploadAccept || undefined}
                    style={{ display: "none" }}
                    onChange={handleContentFileSelect}
                  />

                  {contentInputMode === "write" && (
                    <Box id="create-book-write-panel" px={0} pt={0}>
                      {formData.content?.map((item, index) => (
                        <Box
                          key={index}
                          id={`chapter-item-write-${index}`}
                          p={4}
                          borderRadius="md"
                          mb={4}
                          bg={
                            colorMode === "light"
                              ? "rgba(240, 240, 240, 0.6)"
                              : "rgba(30, 35, 45, 0.6)"
                          }
                          boxShadow="sm"
                        >
                          <HStack justify="flex-end" mb={2} spacing={2}>
                            <IconButton
                              id={`add-chapter-button-write-${index}`}
                              icon={<AddIcon />}
                              aria-label={t(
                                "createBook.form.addChapter",
                                "Add chapter",
                              )}
                              onClick={addContentItem}
                              size="sm"
                              variant="outline"
                              isDisabled={isChapterPipelineBusy}
                            />
                            <IconButton
                              id={`chapter-upload-toggle-write-${index}`}
                              icon={<AttachmentIcon />}
                              aria-label={t(
                                "createBook.content.uploadIntoChapter",
                                "Upload into this chapter",
                              )}
                              isDisabled={isBulkAddingChapters}
                              onClick={() => {
                                setChapterUploadIndex((prev) =>
                                  prev === index ? null : index,
                                );
                              }}
                              size="sm"
                              variant={
                                chapterUploadIndex === index
                                  ? "solid"
                                  : "outline"
                              }
                            />
                            <IconButton
                              id={`remove-chapter-button-write-${index}`}
                              icon={<DeleteIcon />}
                              colorScheme="red"
                              variant="outline"
                              aria-label={t(
                                "createBook.content.remove",
                                "Remove chapter",
                              )}
                              onClick={() => removeContentItem(index)}
                              size="sm"
                            />
                          </HStack>

                          <Stack spacing={3}>
                            <FormControl isRequired>
                              <FormLabel>
                                {t("createBook.form.chapterTitle")}
                              </FormLabel>
                              <Input
                                id={`chapter-title-input-write-${index}`}
                                value={item.chapter}
                                onChange={(e) =>
                                  handleContentChange(
                                    index,
                                    "chapter",
                                    e.target.value,
                                  )
                                }
                                placeholder={t(
                                  "createBook.form.chapterTitlePlaceholder",
                                )}
                                variant="outline"
                              />
                            </FormControl>

                            {chapterUploadIndex === index && (
                              <Box
                                id={`chapter-upload-zone-write-${index}`}
                                borderRadius="md"
                                minHeight="140px"
                                py={3}
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                opacity={isBulkAddingChapters ? 0.5 : 1}
                                pointerEvents={
                                  isBulkAddingChapters ? "none" : "auto"
                                }
                                bg={
                                  isDraggingContentUpload
                                    ? colorMode === "light"
                                      ? "blue.50"
                                      : "rgba(40, 50, 70, 0.6)"
                                    : colorMode === "light"
                                      ? "rgba(240, 240, 240, 0.6)"
                                      : "rgba(30, 35, 45, 0.6)"
                                }
                                _hover={{
                                  bg:
                                    colorMode === "light"
                                      ? "rgba(235, 235, 235, 0.7)"
                                      : "rgba(35, 40, 50, 0.7)",
                                  cursor: "pointer",
                                }}
                                boxShadow={
                                  isDraggingContentUpload
                                    ? "0 0 0 2px var(--chakra-colors-blue-500)"
                                    : "sm"
                                }
                                border={
                                  isDraggingContentUpload
                                    ? "2px dashed"
                                    : "none"
                                }
                                borderColor={
                                  isDraggingContentUpload
                                    ? "blue.500"
                                    : undefined
                                }
                                transition="all 0.2s"
                                onClick={() => {
                                  setPendingChapterUploadIndex(index);
                                  fileInputRef.current?.click();
                                }}
                                onDragEnter={handleContentUploadDragEnter}
                                onDragOver={handleContentUploadDragOver}
                                onDragLeave={handleContentUploadDragLeave}
                                onDrop={(e) =>
                                  handleChapterUploadDrop(e, index)
                                }
                              >
                                <VStack
                                  spacing={2}
                                  px={{ base: 4, md: 2 }}
                                  textAlign="center"
                                >
                                  <AttachmentIcon boxSize={8} />
                                  <Heading size="xs">
                                    {isDraggingContentUpload
                                      ? t(
                                          "createBook.content.dropHere",
                                          "Drop here",
                                        )
                                      : t(
                                          "createBook.content.dropOrClick",
                                          "Drop files here to upload into this chapter",
                                        )}
                                  </Heading>
                                  {chapterUploadTypesLabel ? (
                                    <Text
                                      fontSize="xs"
                                      color={supportedTypesColor}
                                      lineHeight="short"
                                      wordBreak="break-word"
                                    >
                                      {t(
                                        "createBook.content.chapterUploadSupportedTypes",
                                        "Supported types: {{types}}",
                                        {
                                          types: chapterUploadTypesLabel,
                                        },
                                      )}
                                    </Text>
                                  ) : null}
                                </VStack>
                              </Box>
                            )}

                            <FormControl isRequired position="relative">
                              <FormLabel>
                                {t("createBook.form.chapterContent")}
                              </FormLabel>
                              <Box position="relative">
                                <Textarea
                                  id={`chapter-content-textarea-write-${index}`}
                                  value={item.text}
                                  onChange={(e) =>
                                    handleContentChange(
                                      index,
                                      "text",
                                      e.target.value,
                                    )
                                  }
                                  placeholder={t(
                                    "createBook.form.chapterContentPlaceholder",
                                  )}
                                  minH="150px"
                                  variant="outline"
                                />
                                {convertingChapterIndex === index && (
                                  <Box
                                    position="absolute"
                                    inset={0}
                                    borderRadius="md"
                                    bg={
                                      colorMode === "light"
                                        ? "whiteAlpha.900"
                                        : "blackAlpha.800"
                                    }
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    zIndex={1}
                                  >
                                    <VStack spacing={2}>
                                      <Spinner size="lg" />
                                      <Heading size="xs" as="span">
                                        {t(
                                          "createBook.form.processingDocument",
                                          "Processing document…",
                                        )}
                                      </Heading>
                                    </VStack>
                                  </Box>
                                )}
                              </Box>
                            </FormControl>
                          </Stack>
                        </Box>
                      ))}
                      <Button
                        id="create-book-add-chapter-button-write"
                        size="sm"
                        variant="outline"
                        leftIcon={<AddIcon />}
                        isDisabled={isChapterPipelineBusy}
                        onClick={addContentItem}
                      >
                        {t("createBook.form.addChapter", "Add chapter")}
                      </Button>
                    </Box>
                  )}

                  {contentInputMode === "upload" && (
                    <Box id="create-book-upload-panel" px={0} pt={0}>
                      {formData.content?.map((item, index) => {
                        const isCollapsed = collapsedChapters[index] ?? true;
                        return (
                          <Box
                            key={index}
                            id={`chapter-item-upload-${index}`}
                            borderWidth="1px"
                            borderRadius="md"
                            p={3}
                            mb={3}
                          >
                            <Stack
                              direction="row"
                              align="center"
                              justify="space-between"
                              cursor="pointer"
                              onClick={() => toggleChapterCollapsed(index)}
                            >
                              <Heading size="sm">
                                {item.chapter ||
                                  t("createBook.preview.untitled", "Untitled")}
                              </Heading>
                              <IconButton
                                aria-label={isCollapsed ? "Expand" : "Collapse"}
                                icon={
                                  isCollapsed ? (
                                    <ChevronRightIcon />
                                  ) : (
                                    <ChevronDownIcon />
                                  )
                                }
                                size="sm"
                                variant="ghost"
                              />
                            </Stack>
                            {!isCollapsed && (
                              <VStack mt={3} align="stretch" spacing={2}>
                                <Stack
                                  direction="row"
                                  justify="flex-end"
                                  spacing={2}
                                >
                                  <IconButton
                                    id={`add-chapter-button-upload-${index}`}
                                    icon={<AddIcon />}
                                    aria-label={t(
                                      "createBook.form.addChapter",
                                      "Add chapter",
                                    )}
                                    size="sm"
                                    variant="outline"
                                    isDisabled={isChapterPipelineBusy}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      addContentItem();
                                    }}
                                  />
                                  <IconButton
                                    id={`chapter-upload-toggle-${index}`}
                                    icon={<AttachmentIcon />}
                                    aria-label={t(
                                      "createBook.content.uploadIntoChapter",
                                      "Upload into this chapter",
                                    )}
                                    isDisabled={isBulkAddingChapters}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setChapterUploadIndex((prev) =>
                                        prev === index ? null : index,
                                      );
                                    }}
                                    size="sm"
                                    variant={
                                      chapterUploadIndex === index
                                        ? "solid"
                                        : "outline"
                                    }
                                  />
                                  <IconButton
                                    id={`remove-chapter-button-upload-${index}`}
                                    icon={<DeleteIcon />}
                                    colorScheme="red"
                                    variant="outline"
                                    aria-label={t(
                                      "createBook.content.remove",
                                      "Remove chapter",
                                    )}
                                    isDisabled={isChapterPipelineBusy}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeContentItem(index);
                                    }}
                                    size="sm"
                                  />
                                </Stack>
                                <FormControl isRequired>
                                  <FormLabel
                                    htmlFor={`chapter-title-input-upload-${index}`}
                                  >
                                    {t("createBook.form.chapterTitle")}
                                  </FormLabel>
                                  <Input
                                    id={`chapter-title-input-upload-${index}`}
                                    value={item.chapter}
                                    onChange={(e) =>
                                      handleContentChange(
                                        index,
                                        "chapter",
                                        e.target.value,
                                      )
                                    }
                                    placeholder={t(
                                      "createBook.form.chapterTitlePlaceholder",
                                    )}
                                    variant="outline"
                                  />
                                </FormControl>
                                {chapterUploadIndex === index && (
                                  <Box
                                    id={`chapter-upload-zone-${index}`}
                                    borderRadius="md"
                                    minHeight="140px"
                                    py={3}
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    opacity={isBulkAddingChapters ? 0.5 : 1}
                                    pointerEvents={
                                      isBulkAddingChapters ? "none" : "auto"
                                    }
                                    bg={
                                      isDraggingContentUpload
                                        ? colorMode === "light"
                                          ? "blue.50"
                                          : "rgba(40, 50, 70, 0.6)"
                                        : colorMode === "light"
                                          ? "rgba(240, 240, 240, 0.6)"
                                          : "rgba(30, 35, 45, 0.6)"
                                    }
                                    _hover={{
                                      bg:
                                        colorMode === "light"
                                          ? "rgba(235, 235, 235, 0.7)"
                                          : "rgba(35, 40, 50, 0.7)",
                                      cursor: "pointer",
                                    }}
                                    boxShadow={
                                      isDraggingContentUpload
                                        ? "0 0 0 2px var(--chakra-colors-blue-500)"
                                        : "sm"
                                    }
                                    border={
                                      isDraggingContentUpload
                                        ? "2px dashed"
                                        : "none"
                                    }
                                    borderColor={
                                      isDraggingContentUpload
                                        ? "blue.500"
                                        : undefined
                                    }
                                    transition="all 0.2s"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPendingChapterUploadIndex(index);
                                      fileInputRef.current?.click();
                                    }}
                                    onDragEnter={handleContentUploadDragEnter}
                                    onDragOver={handleContentUploadDragOver}
                                    onDragLeave={handleContentUploadDragLeave}
                                    onDrop={(e) =>
                                      handleChapterUploadDrop(e, index)
                                    }
                                  >
                                    <VStack
                                      spacing={2}
                                      px={{ base: 4, md: 2 }}
                                      textAlign="center"
                                    >
                                      <AttachmentIcon boxSize={8} />
                                      <Heading size="xs">
                                        {isDraggingContentUpload
                                          ? t(
                                              "createBook.content.dropHere",
                                              "Drop here",
                                            )
                                          : t(
                                              "createBook.content.dropOrClick",
                                              "Drop files here to upload into this chapter",
                                            )}
                                      </Heading>
                                      {chapterUploadTypesLabel ? (
                                        <Text
                                          fontSize="xs"
                                          color={supportedTypesColor}
                                          lineHeight="short"
                                          wordBreak="break-word"
                                        >
                                          {t(
                                            "createBook.content.chapterUploadSupportedTypes",
                                            "Supported types: {{types}}",
                                            {
                                              types: chapterUploadTypesLabel,
                                            },
                                          )}
                                        </Text>
                                      ) : null}
                                    </VStack>
                                  </Box>
                                )}
                                <FormControl isRequired position="relative">
                                  <FormLabel>
                                    {t("createBook.form.chapterContent")}
                                  </FormLabel>
                                  <Box position="relative">
                                    <Textarea
                                      id={`chapter-content-textarea-upload-${index}`}
                                      value={item.text}
                                      onChange={(e) =>
                                        handleContentChange(
                                          index,
                                          "text",
                                          e.target.value,
                                        )
                                      }
                                      placeholder={t(
                                        "createBook.form.chapterContentPlaceholder",
                                      )}
                                      minH="150px"
                                      rows={6}
                                      variant="outline"
                                    />
                                    {convertingChapterIndex === index && (
                                      <Box
                                        position="absolute"
                                        inset={0}
                                        borderRadius="md"
                                        bg={
                                          colorMode === "light"
                                            ? "whiteAlpha.900"
                                            : "blackAlpha.800"
                                        }
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        zIndex={1}
                                      >
                                        <VStack spacing={2}>
                                          <Spinner size="lg" />
                                          <Heading size="xs" as="span">
                                            {t(
                                              "createBook.form.processingDocument",
                                              "Processing document…",
                                            )}
                                          </Heading>
                                        </VStack>
                                      </Box>
                                    )}
                                  </Box>
                                </FormControl>
                              </VStack>
                            )}
                          </Box>
                        );
                      })}
                      <Button
                        id="create-book-add-chapter-button-upload"
                        size="sm"
                        variant="outline"
                        leftIcon={<AddIcon />}
                        isDisabled={isChapterPipelineBusy}
                        onClick={addContentItem}
                        mt={2}
                        alignSelf="flex-start"
                      >
                        {t("createBook.form.addChapter", "Add chapter")}
                      </Button>

                      <Box id="content-upload-area" alignSelf="stretch" mt={4}>
                        <Text
                          fontSize="sm"
                          color={supportedTypesColor}
                          lineHeight="short"
                          mb={3}
                        >
                          {t(
                            "updateBook.bulkImportIntro",
                            "Upload one or more documents. Text is extracted from each file. You can leave “Split by” empty to get one chapter per file, or split into multiple chapters wherever a whole line matches the text you enter below (for example “Chapter” or “***”).",
                          )}
                        </Text>
                        <VStack align="stretch" spacing={3} mb={3}>
                          <FormControl>
                            <HStack spacing={1} align="center" mb={1}>
                              <FormLabel
                                htmlFor="create-book-bulk-split-delimiter"
                                mb={0}
                              >
                                {t(
                                  "updateBook.bulkSplitDelimiterLabel",
                                  "Split by (line)",
                                )}
                              </FormLabel>
                              <IconButton
                                id="create-book-bulk-split-delimiter-info"
                                aria-label={t(
                                  "updateBook.bulkSplitDelimiterInfoAria",
                                  "More about splitting by line",
                                )}
                                icon={<InfoIcon />}
                                size="sm"
                                variant="ghost"
                                isDisabled={isChapterPipelineBusy}
                                onClick={() =>
                                  toast({
                                    title: t(
                                      "updateBook.bulkSplitDelimiterLabel",
                                      "Split by (line)",
                                    ),
                                    description: t(
                                      "updateBook.bulkSplitDelimiterHelper",
                                      "If set, each uploaded file is split into multiple chapters wherever a line equals this text (spaces at the ends of the line are ignored). Leave empty to keep one chapter per file.",
                                    ),
                                    status: "info",
                                    duration: 8000,
                                    isClosable: true,
                                  })
                                }
                              />
                            </HStack>
                            <Input
                              id="create-book-bulk-split-delimiter"
                              size="sm"
                              value={bulkSplitDelimiter}
                              onChange={(e) =>
                                setBulkSplitDelimiter(e.target.value)
                              }
                              placeholder={t(
                                "updateBook.bulkSplitDelimiterPlaceholder",
                                "e.g. ***",
                              )}
                              isDisabled={isChapterPipelineBusy}
                            />
                          </FormControl>
                        </VStack>
                        <input
                          ref={bulkChaptersFileInputRef}
                          type="file"
                          multiple
                          accept={chapterUploadAccept || undefined}
                          style={{ display: "none" }}
                          onChange={handleBulkChaptersFileSelect}
                        />
                        <Box position="relative">
                          <Box
                            id="content-upload-zone"
                            borderRadius="md"
                            height="180px"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            bg={
                              isDraggingContentUpload
                                ? colorMode === "light"
                                  ? "blue.50"
                                  : "rgba(40, 50, 70, 0.6)"
                                : colorMode === "light"
                                  ? "rgba(240, 240, 240, 0.6)"
                                  : "rgba(30, 35, 45, 0.6)"
                            }
                            _hover={{
                              bg:
                                colorMode === "light"
                                  ? "rgba(235, 235, 235, 0.7)"
                                  : "rgba(35, 40, 50, 0.7)",
                              cursor: isBulkAddingChapters
                                ? "default"
                                : "pointer",
                            }}
                            onClick={() => {
                              if (!isBulkAddingChapters) {
                                bulkChaptersFileInputRef.current?.click();
                              }
                            }}
                            boxShadow={
                              isDraggingContentUpload
                                ? "0 0 0 2px var(--chakra-colors-blue-500)"
                                : "sm"
                            }
                            border={
                              isDraggingContentUpload ? "2px dashed" : "none"
                            }
                            borderColor={
                              isDraggingContentUpload ? "blue.500" : undefined
                            }
                            transition="all 0.2s"
                            opacity={isBulkAddingChapters ? 0.85 : 1}
                            pointerEvents={
                              isBulkAddingChapters ? "none" : "auto"
                            }
                            onDragEnter={handleContentUploadDragEnter}
                            onDragOver={handleContentUploadDragOver}
                            onDragLeave={handleContentUploadDragLeave}
                            onDrop={handleBulkChaptersUploadDrop}
                          >
                            <VStack
                              id="content-upload-zone-text"
                              spacing={2}
                              px={{ base: 4, md: 2 }}
                              textAlign="center"
                            >
                              <AttachmentIcon boxSize={10} />
                              <Text
                                id="content-upload-zone-main-text"
                                fontWeight="medium"
                              >
                                {isDraggingContentUpload
                                  ? t(
                                      "createBook.content.dropHere",
                                      "Drop here",
                                    )
                                  : t(
                                      "createBook.content.dropOrClick",
                                      "Drop files here or click to upload",
                                    )}
                              </Text>
                              {chapterUploadTypesLabel ? (
                                <Text
                                  id="content-upload-zone-hint"
                                  fontSize="xs"
                                  color={supportedTypesColor}
                                  lineHeight="short"
                                  wordBreak="break-word"
                                >
                                  {t(
                                    "createBook.content.chapterUploadSupportedTypes",
                                    "Supported types: {{types}}",
                                    { types: chapterUploadTypesLabel },
                                  )}
                                </Text>
                              ) : null}
                            </VStack>
                          </Box>
                          {isBulkAddingChapters && (
                            <Box
                              position="absolute"
                              inset={0}
                              borderRadius="md"
                              bg={
                                colorMode === "light"
                                  ? "whiteAlpha.900"
                                  : "blackAlpha.800"
                              }
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              zIndex={1}
                            >
                              <VStack spacing={2}>
                                <Spinner size="lg" />
                                <Text fontWeight="medium" fontSize="sm">
                                  {t(
                                    "updateBook.bulkImportLoading",
                                    "Importing…",
                                  )}
                                </Text>
                              </VStack>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>

                <HStack
                  id="book-form-actions"
                  justify="flex-end"
                  spacing={4}
                  pt={4}
                  flexWrap="wrap"
                  gap={2}
                >
                  <Button
                    id="preview-book-button"
                    variant="outline"
                    type="button"
                    whiteSpace="normal"
                    minW={{ base: "min-content", md: "auto" }}
                    onClick={handlePreview}
                  >
                    {t("createBook.form.preview", "Preview")}
                  </Button>
                  <Button
                    id="save-book-button"
                    variant="outline"
                    type="button"
                    whiteSpace="normal"
                    minW={{ base: "min-content", md: "auto" }}
                    isLoading={isSubmitting}
                    loadingText={t("createBook.form.submitting", "Submitting")}
                    spinner={<LoadingLogo size="sm" />}
                    onClick={(e) => handleSubmit(e, false)}
                  >
                    {t("createBook.form.saveBook", "Save Book")}
                  </Button>
                  <Button
                    id="cancel-book-button"
                    variant="outline"
                    type="button"
                    whiteSpace="normal"
                    minW={{ base: "min-content", md: "auto" }}
                    onClick={handleCancel}
                  >
                    {t("createBook.form.cancel")}
                  </Button>
                  <Button
                    id="submit-book-button"
                    colorScheme="blue"
                    type="submit"
                    whiteSpace="normal"
                    minW={{ base: "min-content", md: "auto" }}
                    isLoading={isSubmitting}
                    loadingText={t("createBook.form.submitting", "Submitting")}
                    spinner={<LoadingLogo size="sm" />}
                  >
                    {t("createBook.form.publish", "Publish")}
                  </Button>
                </HStack>
              </Stack>
            </form>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default CreateBook;
